import { io } from 'socket.io-client';
import global from "../global";


const SOCKET_PORT = 5001

// Module-scoped reference so other parts of the app (e.g. file uploads that need to
// pass the live socket id along with an HTTP request) can read the current id.
// Reassigned on each socketConnect; returns null when no active connection.
let activeSocket = null

export const getSocketId = () => activeSocket?.id || null

// BOM editor presence helpers. Promise-based so callers can await the server's ack and branch
// on { ok, editor }. Leave is fire-and-forget — server cleans up on disconnect anyway.
// Ack round-trip budget. A live-but-disconnected socket buffers emits and never acks, which
// previously hung the BOM modal's onMounted forever (blank modal). We instead treat both a
// disconnected socket and an ack timeout as a soft failure so the caller renders the busy banner.
const BOM_EDITOR_ACK_TIMEOUT_MS = 8000

function emitBomEditorEvent(event, projectId) {
    return new Promise((resolve) => {
        // Guard on live connection, not just object existence: activeSocket is assigned before the
        // handshake completes and stays set through reconnect loops, so `!activeSocket` alone lets
        // a red socket through and the ack never returns.
        if (!activeSocket || !activeSocket.connected) {
            return resolve({ ok: false, error: 'no_socket' })
        }
        activeSocket.timeout(BOM_EDITOR_ACK_TIMEOUT_MS).emit(event, { projectId }, (timeoutErr, response) => {
            if (timeoutErr) return resolve({ ok: false, error: 'timeout' })
            resolve(response || { ok: false, error: 'no_response' })
        })
    })
}
export function emitBomEditorJoin(projectId) {
    return emitBomEditorEvent('bom_editor_join', projectId)
}
export function emitBomEditorTakeover(projectId) {
    return emitBomEditorEvent('bom_editor_takeover', projectId)
}
export function emitBomEditorLeave(projectId) {
    if (!activeSocket) return
    activeSocket.emit('bom_editor_leave', { projectId })
}

function getSocketUrl() {
    if (process.env.NODE_ENV === 'development') {
        return `${window.location.protocol}//${window.location.hostname}:${SOCKET_PORT}`
    }
    // Production: no URL, so socket.io connects to the page's own host; nginx routes /socket.io/ there to :5001
    return undefined
}

export function socketConnect(token,secureCode,accessCode) {

    if (token || secureCode || accessCode) {
        const socketUrl = getSocketUrl()
        const socket = io(socketUrl, {
            // Read on every (re)connect, so a socket that dropped while the laptop slept comes back with the
            // renewed token rather than the one it first connected with.
            auth: (cb) => cb({
                token: token ? (global.authenticated.value?.token || token) : null,
                secureCode,
                accessCode
            }),
            reconnection:true,
            reconnectionAttempts:Infinity,
            reconnectionDelay:2000,
            reconnectionDelayMax:5000,
            randomizationFactor:0.5,
            closeOnBeforeunload: true
        });

        activeSocket = socket

        socket.io.on("error",(err)=>{
            console.log('Socket IO Error:')
            console.log(err)
        })

        // Listener to close existing socket if issued new token at relogin before expiration
        window.addEventListener("closeExistingSocket", () => {
            console.log('Closing existing socket')
            socket.close()
            window.removeEventListener('closeExistingSocket', () => { })
        });

        // Listener issues requests to join a room from server
        window.addEventListener("requestRoom", (e) => {
            socket.emit('requestRoomAdmission', { route: e.detail?.route, room: e.detail?.room }, (response) => {
                console.log(response.status)
            })
        })

        // Listener issues request to leave a room from server
        window.addEventListener("leaveRoom", (e) => {
            socket.emit('requestRoomExit', e.detail?.room,)
        })

        window.addEventListener("local_data_change",(e)=>{
            socket.emit('data_change_broadcast',e.detail)
        })

        // Send-only sibling of local_data_change, for a caller that has ALREADY dispatched
        // data_change_from_socket itself to update this tab. No component listens to this name, so the
        // surfaces that bind BOTH event names (the logistics dashboard tiles) fire exactly once instead
        // of twice. Same payload shape and same server hop as local_data_change.
        window.addEventListener("remote_data_change",(e)=>{
            socket.emit('data_change_broadcast',e.detail)
        })

        window.addEventListener("getConnectedUsers", () => {
            socket.emit("getConnectedUsers")
        })

        socket.on("connected_users", (list) => {
            const event = new CustomEvent("connected_users", { detail: list || [] })
            dispatchEvent(event)
        })

        // Lets application know the initial websocket was successful. Used on refresh.
        socket.on("connect", () => {
            global.setAppStatus('ws', true)
            let socketDataEvent = new CustomEvent("socket_connected",{id:'Hi'})
            dispatchEvent(socketDataEvent)
        });

        socket.on("disconnect", () => {
            console.log("%csocket disconnected", "color:red;font-weight: bold;font-size: 12px");
            global.setAppStatus('ws', false)
            socket.removeAllListeners('disconnect');
            socket.removeAllListeners('connection');
        })

        socket.on("connect_error", (err) => {
            console.log(err instanceof Error);
            global.setAppStatus('ws', false)
            console.log(`%csocket connection failed: ${err}`, "color:red;font-weight: bold;font-size: 12px");
            // Message set by the server's socket auth when this user was signed out everywhere.
            if (err?.message === 'socket auth: signed out') { global.logout({ serverEnded: true }) }
            // A refused handshake is not retried by socket.io: renew the token, then connect again.
            if (err?.message === 'socket auth: token expired') {
                const renewThenConnect = () => global.refreshNow().then(result => {
                    if (result === 'renewed') { socket.connect() }
                    else if (result === 'refused') { global.logout({ serverEnded: true }) }
                    else { setTimeout(renewThenConnect, 30000) }
                })
                renewThenConnect()
            }
        });

        // The server signed this user out (password reset, admin lock) — reaches every tab on every device.
        socket.on('signed_out', () => { global.logout({ serverEnded: true }) })

        // A System Admin is taking the server down: the countdown banner (App.vue), or null when cancelled or done.
        socket.on('planned_sign_out', (plan) => { global.setPlannedSignOut(plan) })


        // Status sent from server if db connection is lost\reconnected.
        socket.on("db", (data) => {
            (data) ? console.log('%cServer connection to database connected', "color:green;font-weight: bold;font-size: 12px ") : console.log('%cServer connection to database disconnected', "color:red;font-weight: bold;font-size: 12px ")
            global.setAppStatus('db', data)
        })

        // AI provider health — server-side cron probe broadcasts when ok/degraded flips.
        // Payload: { ok: boolean, lastChecked, latencyMs }. Drives appStatus.aiConnection.
        socket.on("ai_status", (payload) => {
            const ok = !!payload?.ok
            ok ? console.log('%cAI service reachable', "color:green;font-weight: bold;font-size: 12px ") : console.log('%cAI service degraded', "color:red;font-weight: bold;font-size: 12px ")
            global.setAppStatus('ai', ok)
        })

        // received new CRUD data from server and dispatches event to appropriate component.
        socket.on("new_data", (type, room, data) => {
            if (type && room && data) {
                try {
                    const newData = JSON.parse(data)
                    const eventName = 'new_data:' + room
                    console.log(`Sending Event to new_data:${room}`)
                    let socketDataEvent = new CustomEvent(eventName, {
                        detail: { type: type, room: room, data: newData }
                    })
                    dispatchEvent(socketDataEvent)
                } catch (err) {
                    console.error(err)
                    console.error('invalid data received via socket request')
                }
            } else {
                console.log('socket request missing data')
            }
        })

        socket.on("queue_update", (e)=>{
                try {
                    const data = JSON.parse(e)
                    if(data.queue && data.count) {
                        let socketDataEvent = new CustomEvent(data.queue,{
                            detail: {data: data.count}
                        })
                        dispatchEvent(socketDataEvent)
                    }
                } catch (err) {
                    console.error('Invalid Data received in queue update socket.')
                }
        })

        socket.on("socket_data_change_trigger",(event)=>{
            console.log('Received data change from client. Socket ID:', socket.id);
            let changeTriggerEvent = new CustomEvent("data_change_from_socket",{detail:event});
            dispatchEvent(changeTriggerEvent);
            console.log(`Received Event From another Client.`)
        })



        // BOM editor presence — direct-to-socket notification when another user takes over the
        // editor slot for a project we currently hold. Fanned out as a window event so the modal
        // (or any other interested component) can react without subscribing to the raw socket.
        socket.on('bom_editor_revoked', (payload) => {
            dispatchEvent(new CustomEvent('bom_editor_revoked', { detail: payload || {} }))
        })

        socket.on('process_complete',(event)=>{
            if(event) {
                let socketDataEvent = new CustomEvent('process_complete',{
                    detail: {data: event}
                })
                dispatchEvent(socketDataEvent)
            }
        })

        socket.on('process_progress',(event)=>{
            if(event) {
                dispatchEvent(new CustomEvent('process_progress',{
                    detail: {data: event}
                }))
            }
        })

        // Message from server on successful socket connection.
        socket.on("welcomeMessage", (data) => { console.log(`%c${data}`, "color:green;font-weight: bold;font-size: 12px ") })

        // Logs message from server
        socket.on('message', (data) => {
            console.log(data)
        })

    } else {
        console.error('Missing Token or Code.')
    }
}




